import { queryOptions, useQuery } from "@tanstack/react-query";

import data from "../../assets/mocks/notifications.json";

import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";
import { AxiosMock } from "@/utils/mock";

import type { QueryConfig } from "@/lib/react-query";
import type {
  Notification,
  NotificationApi,
  NotificationStatus,
  NotificationsResponse,
  NotificationsResponseApi,
} from "@/types/notification";

if (import.meta.env.MODE === "test") {
  const mock = AxiosMock.getInstance();
  mock.onGet("/carburate/notifications/messages/").reply(200, data);
}

export type GetNotificationsParams = {
  page?: number;
  pageSize?: number;
  status?: NotificationStatus;
};

export const getNotifications = ({
  page = 1,
  pageSize = 999999,
  status,
}: GetNotificationsParams = {}) => {
  return api
    .get("/carburate/notifications/messages/", {
      params: {
        page,
        page_size: pageSize,
        ...(status ? { status } : {}),
      },
    })
    .then((response) => {
      // `api` response already returns `response.data`
      const data = response as unknown;

      // Some environments may return an array directly
      if (Array.isArray(data)) {
        const results = (data as NotificationApi[]).map(mapNotificationApiToUi);
        return {
          page: 1,
          page_size: results.length,
          total: results.length,
          results,
        } satisfies NotificationsResponse;
      }

      const payload = data as NotificationsResponseApi;
      return {
        page: payload.page ?? page,
        page_size: payload.page_size ?? pageSize,
        total: payload.total ?? 0,
        results: (payload.results ?? []).map(mapNotificationApiToUi),
      } satisfies NotificationsResponse;
    });
};

function mapNotificationApiToUi(n: NotificationApi): Notification {
  return {
    id: n.message_id,
    user_id: n.user_id,
    date: n.created_at,
    message: n.data?.message ?? "",
    status: n.status,
  };
}

export const getNotificationsQueryOptions = (params: GetNotificationsParams = {}) => {
  return queryOptions({
    queryKey: ["notifications", params],
    queryFn: () => getNotifications(params),
    staleTime: 0,
    gcTime: 0,
    refetchInterval: 5000,
  });
};

type UseNotificationsOptions = {
  page?: number;
  pageSize?: number;
  status?: NotificationStatus;
  queryConfig?: QueryConfig<typeof getNotifications>;
};

export const useNotifications = ({
  page,
  pageSize,
  status,
  queryConfig,
}: UseNotificationsOptions = {}) => {
  return useQuery({
    enabled: hasAccessToken(),
    ...getNotificationsQueryOptions({ page, pageSize, status }),
    refetchOnWindowFocus: true,
    ...queryConfig,
  });
};
